import { useState, useEffect } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import TextField from '@mui/material/TextField';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Link } from 'react-router-dom';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const SiteSettingsAdmin = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const { user } = useSelector((state) => state.user);
    const isSuperAdmin = user.role === "admin";
    const perms = user.adminRole?.permissions?.siteContent || {};
    const canEdit = isSuperAdmin || perms.edit;

    const [trustTitle, setTrustTitle] = useState("");
    const [trustTitleZh, setTrustTitleZh] = useState("");
    const [trustBody, setTrustBody] = useState("");
    const [trustBodyZh, setTrustBodyZh] = useState("");
    const [footerPhone, setFooterPhone] = useState("");
    const [footerHours, setFooterHours] = useState("");
    const [facebook, setFacebook] = useState("");
    const [twitter, setTwitter] = useState("");
    const [instagram, setInstagram] = useState("");

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const { data } = await axios.get('/api/v1/site-settings');
                const s = data.settings;
                setTrustTitle(s.trustTitle || "");
                setTrustTitleZh(s.trustTitleZh || "");
                setTrustBody(s.trustBody || "");
                setTrustBodyZh(s.trustBodyZh || "");
                setFooterPhone(s.footerPhone || "");
                setFooterHours(s.footerHours || "");
                setFacebook(s.footerSocial?.facebook || "");
                setTwitter(s.footerSocial?.twitter || "");
                setInstagram(s.footerSocial?.instagram || "");
            } catch (error) {
                enqueueSnackbar("Failed to load site settings", { variant: "error" });
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, [enqueueSnackbar]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await axios.put('/api/v1/admin/site-settings', {
                trustTitle,
                trustTitleZh,
                trustBody,
                trustBodyZh,
                footerPhone,
                footerHours,
                footerSocial: { facebook, twitter, instagram },
            });
            enqueueSnackbar("Site settings updated", { variant: "success" });
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to save", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <BackdropLoader />;

    return (
        <>
            <MetaData title="Admin: Site Content | 1313 Mart" />
            {saving && <BackdropLoader />}

            <div className="flex flex-wrap justify-end gap-3 mb-4">
                <Link to="/admin/product-sections" className="py-2 px-4 rounded shadow font-medium text-white bg-indigo-600 hover:shadow-lg">Manage Product Sections</Link>
                <Link to="/admin/banner-slides" className="py-2 px-4 rounded shadow font-medium text-white bg-primary-blue hover:shadow-lg">Manage Banner Slides</Link>
                <Link to="/admin/promo-cards" className="py-2 px-4 rounded shadow font-medium text-white bg-primary-green hover:shadow-lg">Manage Promo Cards</Link>
                <Link to="/admin/trust-promos" className="py-2 px-4 rounded shadow font-medium text-white bg-gray-700 hover:shadow-lg">Manage Trust Promos</Link>
            </div>

            <div className="flex flex-col gap-6 bg-white rounded-lg shadow p-4 sm:p-6">

                <h2 className="font-medium text-lg">Trust Section</h2>
                <TextField
                    label="Trust Section Title"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={trustTitle}
                    onChange={(e) => setTrustTitle(e.target.value)}
                    disabled={!canEdit}
                />
                <TextField
                    label="Trust Section Title (中文 — optional)"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={trustTitleZh}
                    onChange={(e) => setTrustTitleZh(e.target.value)}
                    disabled={!canEdit}
                />
                <div>
                    <p className="text-sm text-gray-600 mb-1">Trust Section Description</p>
                    <ReactQuill theme="snow" value={trustBody} onChange={setTrustBody} readOnly={!canEdit} />
                </div>
                <div>
                    <p className="text-sm text-gray-600 mb-1">Trust Section Description (中文 — optional)</p>
                    <ReactQuill theme="snow" value={trustBodyZh} onChange={setTrustBodyZh} readOnly={!canEdit} />
                </div>

                <h2 className="font-medium text-lg">Footer Info</h2>
                <div className="flex flex-col sm:flex-row gap-4">
                    <TextField
                        label="Phone Number"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={footerPhone}
                        onChange={(e) => setFooterPhone(e.target.value)}
                        disabled={!canEdit}
                    />
                    <TextField
                        label="Store Hours"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={footerHours}
                        onChange={(e) => setFooterHours(e.target.value)}
                        disabled={!canEdit}
                    />
                </div>

                <h2 className="font-medium text-lg">Social Links</h2>
                <div className="flex flex-col sm:flex-row gap-4">
                    <TextField label="Facebook URL" variant="outlined" size="small" fullWidth value={facebook} onChange={(e) => setFacebook(e.target.value)} disabled={!canEdit} />
                    <TextField label="Twitter URL" variant="outlined" size="small" fullWidth value={twitter} onChange={(e) => setTwitter(e.target.value)} disabled={!canEdit} />
                    <TextField label="Instagram URL" variant="outlined" size="small" fullWidth value={instagram} onChange={(e) => setInstagram(e.target.value)} disabled={!canEdit} />
                </div>

                {canEdit && (
                    <div className="flex justify-end">
                        <button onClick={handleSave} className="bg-primary-orange uppercase px-6 py-3 text-white font-medium rounded shadow hover:shadow-lg">
                            Save Changes
                        </button>
                    </div>
                )}

            </div>
        </>
    );
};

export default SiteSettingsAdmin;